import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_COLLAPSED_MIN_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH_RATIO,
  SIDEBAR_EXPANDED_WIDTH,
  SIDEBAR_EXPANDED_MIN_WIDTH,
  SIDEBAR_EXPANDED_WIDTH_RATIO,
  DEFAULT_VIEWPORT_WIDTH,
  LOGO_SOURCES,
} from './constants';
import { PlaceholderVariant } from './types';

// Sidebar logo preloading
let sidebarLogosPreloaded = false;

export const ensureSidebarLogosPreloaded = () => {
  if (sidebarLogosPreloaded || typeof window === 'undefined' || typeof Image === 'undefined') {
    return;
  }

  sidebarLogosPreloaded = true;
  LOGO_SOURCES.forEach((src) => {
    const img = new Image();
    img.src = src;
    // img.decode() removed — causes "Can't open this page" crash on iOS WebKit
    // after 2-3 page reloads (see PR #571). Setting img.src is sufficient.
  });
};

// Initialize preloading on module load
if (typeof window !== 'undefined') {
  ensureSidebarLogosPreloaded();
}

// Viewport and sidebar width utilities
export const getViewportWidth = (): number => {
  if (typeof window === 'undefined') {
    return DEFAULT_VIEWPORT_WIDTH;
  }

  return (
    window.visualViewport?.width ??
    window.innerWidth ??
    document.documentElement?.clientWidth ??
    DEFAULT_VIEWPORT_WIDTH
  );
};

export const getResponsiveSidebarWidth = (collapsed: boolean, viewportWidth: number): number => {
  const ratio = collapsed ? SIDEBAR_COLLAPSED_WIDTH_RATIO : SIDEBAR_EXPANDED_WIDTH_RATIO;
  const minWidth = collapsed ? SIDEBAR_COLLAPSED_MIN_WIDTH : SIDEBAR_EXPANDED_MIN_WIDTH;
  const maxWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  const scaledWidth = viewportWidth * ratio;
  return Math.round(Math.min(maxWidth, Math.max(minWidth, scaledWidth)));
};

// File type utilities
export const isPdfFile = (filename: string): boolean => {
  return filename.toLowerCase().endsWith('.pdf');
};

export const isImageFile = (filename: string): boolean => {
  const lower = filename.toLowerCase();
  return (
    lower.endsWith('.png') ||
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg') ||
    lower.endsWith('.gif') ||
    lower.endsWith('.bmp') ||
    lower.endsWith('.webp') ||
    lower.endsWith('.tiff') ||
    lower.endsWith('.svg')
  );
};

export const getTextPlaceholderVariant = (filename: string): 'textCsv' | 'textTxt' | 'textMd' | null => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.csv')) return 'textCsv';
  if (lower.endsWith('.txt')) return 'textTxt';
  if (lower.endsWith('.md')) return 'textMd';
  return null;
};

export const getPlaceholderVariant = (filename: string): PlaceholderVariant => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.docx')) return 'docx';
  if (lower.endsWith('.xlsx')) return 'xlsx';
  if (isPdfFile(filename)) return 'pdf';
  if (isImageFile(filename)) return 'image';
  const textVariant = getTextPlaceholderVariant(filename);
  if (textVariant) return textVariant;
  return 'file';
};

export const isTextLikeFile = (file: File): boolean => {
  const type = file.type?.toLowerCase?.() ?? '';
  if (type === 'text/plain') return true;
  if (type === 'text/markdown') return true;
  if (type === 'text/csv' || type === 'application/csv' || type === 'application/vnd.ms-excel') return true;
  if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true;
  if (type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return true;
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith('.docx') || lowerName.endsWith('.xlsx')) return true;
  return getTextPlaceholderVariant(file.name) !== null;
};

export const isPageCountableFile = (filename: string): boolean => {
  const variant = getPlaceholderVariant(filename);
  return (
    variant === 'pdf' ||
    variant === 'image' ||
    variant === 'textCsv' ||
    variant === 'textTxt' ||
    variant === 'textMd' ||
    variant === 'xlsx'
  );
};

export const shouldShowPageCount = (filename: string, pageCount?: number): boolean => {
  if (!isPageCountableFile(filename) || typeof pageCount !== 'number') {
    return false;
  }

  const variant = getPlaceholderVariant(filename);
  const shouldHideSinglePage = variant === 'xlsx' || variant === 'textTxt' || variant === 'textMd';

  if (shouldHideSinglePage && pageCount <= 1) {
    return false;
  }

  return true;
};

// File key generation
export const getFileKey = (file: File): string => `${file.name}-${file.lastModified}`;

// Page count formatting
export const formatPageCountLabel = (count: number): string => `${count} página${count === 1 ? '' : 's'}`;

// Chat title formatting
export const formatChatTitle = (raw: string): string => {
  if (!raw) return '';
  // Remove most emoji and pictographs
  const noEmoji = raw.replace(/[\u{1F300}-\u{1FAFF}\u{1F900}-\u{1F9FF}\u{2600}-\u{27BF}\u{FE0F}]/gu, '');
  // Collapse whitespace
  let t = noEmoji.replace(/\s+/g, ' ').trim();
  // Remove excessive trailing punctuation
  t = t.replace(/[.,;:!?…—-]+$/u, '').trim();
  // Capitalize first letter, keep the rest as-is
  if (t.length > 0) t = t.charAt(0).toUpperCase() + t.slice(1);
  // Truncate to a clean length
  const limit = 50;
  if (t.length > limit) t = t.slice(0, limit).trimEnd() + '…';
  return t;
};

// Attachment grid size calculation
export const getAttachmentThumbSize = (count: number): number => {
  if (count <= 1) return 160;
  if (count <= 2) return 148;
  if (count <= 4) return 128;
  if (count <= 6) return 108;
  return 92;
};

// Base64 file conversion
export const getBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

// Chat grouping utilities
export const getGroupLabel = (updatedAtOrTitle?: string): string => {
  if (!updatedAtOrTitle) return 'Semanas anteriores';

  let parsed: Date | null = null;
  const iso = new Date(updatedAtOrTitle);

  if (!isNaN(iso.getTime())) {
    parsed = iso;
  } else {
    const m = updatedAtOrTitle.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[\-\s](\d{2}):(\d{2}):(\d{2}))?$/);
    if (m) {
      const [, dd, mm, yyyy, HH = '00', MM = '00', SS = '00'] = m;
      parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(HH), Number(MM), Number(SS));
    }
  }

  if (!parsed || isNaN(parsed.getTime())) return 'Semanas anteriores';

  const now = new Date();
  const toMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = toMidnight(now);
  const day = toMidnight(parsed);
  const diffMs = today.getTime() - day.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays <= 7) return 'Essa semana';
  if (diffDays <= 14) return 'Semana passada';
  return 'Semanas anteriores';
};

export const GROUP_ORDER = ['Hoje', 'Ontem', 'Essa semana', 'Semana passada', 'Semanas anteriores'];

/**
 * Append a freshly-fetched page of chats to the existing sidebar list without
 * introducing duplicate chat_ids.
 *
 * `/api/chat/list` paginates by raw position in the user's `chats` array but
 * filters out empty chats *after* slicing, so a page often returns fewer items
 * than the requested `limit`. `fetchMoreChats` advances the next request's
 * `offset` by the number of chats it actually received, which lags the raw
 * positions the server already consumed — so consecutive pages overlap at the
 * boundary. Blindly concatenating them (`[...userChats, ...data.chats]`) put
 * the boundary chat in the list twice under one chat_id, which rendered a
 * duplicate row and made "delete one" delete both. Keep the first occurrence
 * and append only chat_ids not already present.
 */
export const mergeUniqueChats = (existing: any[], incoming: any[]): any[] => {
  const merged = Array.isArray(existing) ? [...existing] : [];
  const seen = new Set(merged.map((c) => c?.chat_id).filter(Boolean));
  (Array.isArray(incoming) ? incoming : []).forEach((chat) => {
    const id = chat?.chat_id;
    if (id && seen.has(id)) return;
    if (id) seen.add(id);
    merged.push(chat);
  });
  return merged;
};

export interface GroupedChatItem {
  type: 'header' | 'chat';
  label?: string;
  chat?: any;
}

export const buildGroupedItems = (chats: any[]): GroupedChatItem[] => {
  const groups: Record<string, any[]> = {
    Hoje: [],
    Ontem: [],
    'Essa semana': [],
    'Semana passada': [],
    'Semanas anteriores': [],
  };

  chats.forEach((c) => {
    const label = getGroupLabel(c?.updated_at || c?.title);
    if (!groups[label]) groups[label] = [];
    groups[label].push(c);
  });

  const items: GroupedChatItem[] = [];
  GROUP_ORDER.forEach((label) => {
    const arr = groups[label] || [];
    if (arr.length > 0) {
      items.push({ type: 'header', label });
      arr.forEach((chat) => items.push({ type: 'chat', chat }));
    }
  });

  return items;
};

// Latex preprocessing
// Message content is not always a string: server-side flows persist the
// structured form (a list of content blocks) for user messages too. Every
// handler that needs the message TEXT (render, copy, edit, regenerate) goes
// through this normalizer — string ops on the raw content crashed the app
// when the array form showed up (Sentry CHAT-MARITACA-2T).
export const getMessageTextContent = (content: unknown): string => {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .filter((item: any) => item && item.type === 'text' && typeof item.text === 'string')
      .map((item: any) => item.text)
      .join('\n');
  }
  if (content == null) return '';
  return String(content);
};

// A `$` is only math if it opens a span that also CLOSES as math. Deciding on
// the lone character (the old escape of any `$` before a digit) broke real
// formulas that start with a digit — in `$0 \in K$ (geralmente), mas $u \in K$`
// the opener got escaped and remark-math paired the remaining dollars across
// the prose between the formulas. Pair rules follow pandoc's tex_math_dollars:
// the opener has a non-space to its right, the closer has a non-space to its
// left and no digit to its right (kills "de $10 a $20"), the pair sits on one
// line and the span is capped. Every `$` left unpaired is escaped, so
// remark-math (which pairs like code spans and would take "10 a" as math)
// never sees it. `$$` passes through untouched; code spans/fences are skipped.
const MAX_INLINE_MATH_SPAN = 200;

// The FIRST `$` after the opener decides: either it closes a valid span or
// there is no span at all. Scanning past an invalid candidate would glue
// "custa $100 mas o custo real é $x$" into one bogus formula; giving up
// leaves $100 literal and lets $x$ pair on its own.
const findMathCloser = (line: string, opener: number): number => {
  const right = line[opener + 1];
  if (!right || right === ' ' || right === '\t') return -1;
  let j = opener + 1;
  while (j < line.length && j - opener <= MAX_INLINE_MATH_SPAN) {
    const c = line[j];
    if (c === '\\') {
      j += 2;
      continue;
    }
    if (c === '`') return -1;
    if (c === '$') {
      const left = line[j - 1];
      const after = line[j + 1];
      const leftOk = left !== ' ' && left !== '\t';
      const afterOk = !(after >= '0' && after <= '9');
      return leftOk && afterOk ? j : -1;
    }
    j += 1;
  }
  return -1;
};

const escapeNonMathDollarsInLine = (line: string): string => {
  let out = '';
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    if (ch === '\\') {
      out += line.slice(i, i + 2);
      i += 2;
      continue;
    }
    if (ch === '`') {
      const run = /^`+/.exec(line.slice(i))![0];
      const close = line.indexOf(run, i + run.length);
      const end = close === -1 ? line.length : close + run.length;
      out += line.slice(i, end);
      i = end;
      continue;
    }
    if (ch !== '$') {
      out += ch;
      i += 1;
      continue;
    }
    if (line[i + 1] === '$') {
      out += '$$';
      i += 2;
      continue;
    }
    const closer = findMathCloser(line, i);
    if (closer !== -1) {
      out += line.slice(i, closer + 1);
      i = closer + 1;
    } else {
      out += '\\$';
      i += 1;
    }
  }
  return out;
};

export const escapeNonMathDollars = (text: string): string => {
  let inFence = false;
  return text
    .split('\n')
    .map((line) => {
      if (/^\s*(```|~~~)/.test(line)) {
        inFence = !inFence;
        return line;
      }
      return inFence ? line : escapeNonMathDollarsInLine(line);
    })
    .join('\n');
};

export const preprocessingLatex = (text: string): string => {
  const normalized = text
    .replace(/\\\\\[/g, '$$$$')
    .replace(/\\\\\]/g, '$$$$')
    .replace(/\\\\\(/g, '$$')
    .replace(/\\\\\)/g, '$$')
    .replace(/\\\[/g, '$$$$')
    .replace(/\\\]/g, '$$$$')
    .replace(/\\\(/g, '$$')
    .replace(/\\\)/g, '$$')
    // R$/US$ before a number is always currency; taking these out of play
    // before the pair scan keeps a price pair like "R$5 e R$ 10" from ever
    // being considered as a span.
    .replace(/(R|US)\$(?=\s*\d)/g, (_m, prefixo) => prefixo + '\\$');
  return escapeNonMathDollars(normalized);
};
